import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Users, CreditCard, TrendingUp, Globe, Shield,
  Search, RefreshCw, UserCheck, Crown, AlertCircle,
  Server, CheckCircle2, XCircle, MessageSquare
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, profile, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats]         = useState(null);
  const [health, setHealth]       = useState(null);
  const [users, setUsers]         = useState([]);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [actionMsg, setActionMsg] = useState('');

  // Wait for auth to resolve before redirecting
  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;
  if (!user)            return <Navigate to="/auth" replace />;
  if (profile && !isAdmin()) return <Navigate to="/" replace />;

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    await Promise.all([loadStats(), loadUsers(), loadHealth()]);
    setLoading(false);
  }

  async function loadHealth() {
    try {
      const res = await fetch('/api/health');
      if (res.ok) setHealth(await res.json());
    } catch (e) {
      console.error('Health check failed:', e);
    }
  }

  async function loadStats() {
    try {
      const { data: allProfiles } = await supabase
        .from('profiles').select('id, is_admin, subscription_status, created_at');

      const { data: subs } = await supabase
        .from('subscriptions').select('amount, created_at').order('created_at', { ascending: false });

      const { data: sites } = await supabase
        .from('generated_websites').select('id, created_at');

      const now    = new Date();
      const week   = new Date(now - 7  * 86400000);
      const month  = new Date(now - 30 * 86400000);
      const total  = allProfiles?.length || 0;
      const paid   = allProfiles?.filter(p => p.subscription_status === 'paid').length || 0;
      const admins = allProfiles?.filter(p => p.is_admin).length || 0;
      const newWeek  = allProfiles?.filter(p => new Date(p.created_at) > week).length  || 0;
      const newMonth = allProfiles?.filter(p => new Date(p.created_at) > month).length || 0;
      const revenue  = subs?.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0) || 0;

      setStats({
        total, paid, free: total - paid - admins, admins,
        newWeek, newMonth,
        revenue: revenue.toFixed(2),
        recentSubs: subs?.slice(0, 5) || [],
        totalSites: sites?.length || 0,
      });
    } catch (e) {
      console.error('Stats error:', e);
    }
  }

  async function loadUsers() {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id, email, full_name, is_admin, subscription_status, created_at, updated_at')
        .order('created_at', { ascending: false });
      setUsers(data || []);
    } catch (e) {
      console.error('Users error:', e);
    }
  }

  async function toggleAdmin(userId, current) {
    const { error } = await supabase
      .from('profiles')
      .update({ is_admin: !current })
      .eq('id', userId);
    if (!error) { setActionMsg('Admin status updated'); loadUsers(); loadStats(); }
  }

  async function togglePaid(userId, current) {
    const newStatus = current === 'paid' ? 'free' : 'paid';
    const { error } = await supabase
      .from('profiles')
      .update({ subscription_status: newStatus })
      .eq('id', userId);
    if (!error) { setActionMsg(`User set to ${newStatus}`); loadUsers(); loadStats(); }
  }

  const filtered = users.filter(u =>
    !search || u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const StatCard = ({ icon: Icon, label, value, sub, color = 'text-blue-400' }) => (
    <Card className="bg-gray-800/50 border-gray-700 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{label}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-gray-700/50`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </Card>
  );

  const roleBadge = (u) => {
    if (u.is_admin) return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">ADMIN</Badge>;
    if (u.subscription_status === 'paid') return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">PRO</Badge>;
    return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">FREE</Badge>;
  };

  return (
    <div className="min-h-screen p-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-400" /> Admin Dashboard
          </h1>
          <p className="text-gray-400 mt-1">globalnow.biz platform management</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          {actionMsg && (
            <span className="text-green-400 text-sm">✅ {actionMsg}</span>
          )}
          <Button onClick={loadAll} variant="outline" className="border-gray-600 text-gray-300" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Link to="/admin/models">
            <Button variant="outline" className="border-gray-600 text-gray-300" size="sm">
              🤖 AI Models
            </Button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-20">Loading platform data…</div>
      ) : (
        <>
          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <StatCard icon={Users}      label="Total Users"    value={stats?.total}      color="text-blue-400" />
            <StatCard icon={Crown}      label="PRO Members"    value={stats?.paid}       color="text-yellow-400" sub="paying customers" />
            <StatCard icon={UserCheck}  label="Free Users"     value={stats?.free}       color="text-gray-400" />
            <StatCard icon={Shield}     label="Admins"         value={stats?.admins}     color="text-red-400" />
            <StatCard icon={TrendingUp} label="New This Week"  value={stats?.newWeek}    color="text-green-400" sub={`${stats?.newMonth} this month`} />
            <StatCard icon={CreditCard} label="Revenue"        value={`$${stats?.revenue}`} color="text-emerald-400" sub="all time" />
          </div>

          {/* Second row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-gray-800/50 border-gray-700 p-5">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-400" /> Platform Usage
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Websites generated</span><span className="text-white font-semibold">{stats?.totalSites}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Conversion rate</span>
                  <span className="text-yellow-400 font-semibold">
                    {stats?.total > 0 ? ((stats.paid / stats.total) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="flex justify-between"><span className="text-gray-400">Free → PRO</span><span className="text-gray-400">$149.99 LTD</span></div>
              </div>
            </Card>

            <Card className="bg-gray-800/50 border-gray-700 p-5">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-emerald-400" /> Recent Payments
              </h3>
              {stats?.recentSubs?.length ? (
                <div className="space-y-2 text-sm">
                  {stats.recentSubs.map((s, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-gray-400">{new Date(s.created_at).toLocaleDateString()}</span>
                      <span className="text-emerald-400 font-semibold">${s.amount}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No payments yet</p>
              )}
            </Card>

            <Card className="bg-gray-800/50 border-gray-700 p-5">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-400" /> Quick Actions
              </h3>
              <div className="space-y-2">
                <Link to="/admin/models" className="block">
                  <Button size="sm" variant="outline" className="w-full border-gray-600 text-gray-300 justify-start">
                    🤖 Manage AI Models
                  </Button>
                </Link>
                <Link to="/upgrade" className="block">
                  <Button size="sm" variant="outline" className="w-full border-gray-600 text-gray-300 justify-start">
                    💳 View Upgrade Page
                  </Button>
                </Link>
                <Link to="/models" className="block">
                  <Button size="sm" variant="outline" className="w-full border-gray-600 text-gray-300 justify-start">
                    📊 Model Database
                  </Button>
                </Link>
              </div>
            </Card>
          </div>

          {/* Service Health */}
          {health && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <Card className="bg-gray-800/50 border-gray-700 p-5">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Server className="w-4 h-4 text-purple-400" /> Service Health
                </h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(health.services || {}).map(([svc, status]) => (
                    <div key={svc} className="flex justify-between items-center">
                      <span className="text-gray-400 capitalize">{svc}</span>
                      <span className={`flex items-center gap-1 font-semibold ${
                        status === 'ok' || status === 'configured' ? 'text-green-400' :
                        status === 'live' ? 'text-emerald-400' :
                        status === 'sandbox' ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {(status === 'ok' || status === 'configured') ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        {status}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="bg-gray-800/50 border-gray-700 p-5">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-400" /> Backend Stats
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-400">Paid users (MongoDB)</span><span className="text-white font-semibold">{health.stats?.paid_users}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Total payments</span><span className="text-emerald-400 font-semibold">{health.stats?.total_payments}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Support chats</span><span className="text-blue-400 font-semibold">{health.stats?.support_chats}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Waitlist signups</span><span className="text-purple-400 font-semibold">{health.stats?.waitlist_signups ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Revenue (MongoDB)</span><span className="text-emerald-400 font-semibold">${health.stats?.total_revenue}</span></div>
                  <div className="flex justify-between text-xs mt-2"><span className="text-gray-600">Last checked</span><span className="text-gray-600">{new Date(health.timestamp).toLocaleTimeString()}</span></div>
                </div>
              </Card>
            </div>
          )}

          {/* Users table */}
          <Card className="bg-gray-800/50 border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" /> All Users ({users.length})
              </h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by email or name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white pl-9 w-64"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left py-2 pr-4">User</th>
                    <th className="text-left py-2 pr-4">Role</th>
                    <th className="text-left py-2 pr-4">Joined</th>
                    <th className="text-left py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                      <td className="py-3 pr-4">
                        <div className="font-medium text-white">{u.full_name || '—'}</div>
                        <div className="text-gray-400 text-xs">{u.email}</div>
                      </td>
                      <td className="py-3 pr-4">{roleBadge(u)}</td>
                      <td className="py-3 pr-4 text-gray-400">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2 flex-wrap">
                          {/* Don't allow demoting yourself */}
                          {u.id !== user?.id && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className={`border-gray-600 text-xs h-7 ${u.is_admin ? 'text-red-400 border-red-500/30' : 'text-gray-300'}`}
                                onClick={() => toggleAdmin(u.id, u.is_admin)}
                              >
                                {u.is_admin ? '− Admin' : '+ Admin'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className={`border-gray-600 text-xs h-7 ${u.subscription_status === 'paid' ? 'text-yellow-400 border-yellow-500/30' : 'text-gray-300'}`}
                                onClick={() => togglePaid(u.id, u.subscription_status)}
                              >
                                {u.subscription_status === 'paid' ? '− PRO' : '+ PRO'}
                              </Button>
                            </>
                          )}
                          {u.id === user?.id && (
                            <span className="text-gray-500 text-xs italic">you</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <p className="text-gray-500 text-center py-8">No users match your search</p>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
